import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChatView } from '../components/ChatView';
import { useConversations } from '../hooks/useConversations';
import { ConversationsService } from '../services/conversationsService';
import { mockConversations, mockMessages } from '../data/mockChats';
import { User } from '../types';
import { ConnectionService } from '../services/connectionService';
import { ROUTES } from '../constants/app';

interface ChatPageProps {
  currentUser: User;
}

/**
 * VISTA: Página de chat individual
 * 
 * Funcionalidades principales:
 * - Mostrar conversación específica basada en ID de URL
 * - Enviar mensajes y actualizar conversación
 * - Navegación de regreso a lista de conversaciones
 */
export const ChatPage: React.FC<ChatPageProps> = ({ currentUser }) => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const conversationManager = useConversations();
  const [conversation, setConversation] = useState<Conversation | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  // Cargar conversación al montar el componente
  useEffect(() => {
    const loadConversation = async () => {
      if (!conversationId) {
        console.error('❌ No se proporcionó ID de conversación');
        setIsLoading(false);
        return;
      }

      try {
        console.log('🔍 Cargando conversación:', conversationId);
        
        // Primero intentar cargar desde Supabase
        const conv = await conversationManager.getConversationById(conversationId, currentUser.id);
        
        if (conv) {
          console.log('✅ Conversación cargada exitosamente:', conv.id);
          setConversation(conv);
        } else {
          console.warn('⚠️ Conversación no encontrada, intentando cargar desde localStorage...');
          
          // Fallback: buscar en localStorage
          const localConversations = JSON.parse(localStorage.getItem('racksMatchConversations') || '[]');
          const localConv = localConversations.find((c: any) => c.id === conversationId);
          
          if (localConv) {
            console.log('✅ Conversación encontrada en localStorage');
            setConversation(localConv);
          } else {
            console.error('❌ Conversación no encontrada en ningún lado');
          }
        }
      } catch (error) {
        console.error('❌ Error al cargar conversación:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadConversation();
  }, [conversationId, currentUser.id]);

  /**
   * Maneja el envío de mensajes en la conversación actual
   */
  const handleSendMessage = useCallback((content: string) => {
    if (!conversation || !conversationId) return;
    
    // Obtener el ID del otro usuario de los participantes
    if (conversation.participants.length < 2) {
      console.error('❌ No hay suficientes participantes en la conversación');
      return;
    }
    
    const otherUser = conversation.participants.find(p => p.id !== currentUser.id);
    if (!otherUser) {
      console.error('❌ No se pudo encontrar el otro usuario en la conversación');
      return;
    }
    
    // Crear mensaje optimista para mostrar inmediatamente
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      senderId: currentUser.id,
      receiverId: otherUser.id,
      content,
      timestamp: new Date(),
      read: false
    };

    // Actualizar UI inmediatamente
    setConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...prev.messages, optimisticMessage],
        lastMessage: optimisticMessage,
        updatedAt: new Date()
      };
    });

    // Enviar mensaje a Supabase en background
    ConversationsService.sendMessage(
      conversationId,
      currentUser.id,
      otherUser.id,
      content
    ).then(result => {
      if (result.success && result.message) {
        // Reemplazar mensaje optimista con el real
        setConversation(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map(msg => 
              msg.id === optimisticMessage.id ? result.message! : msg
            )
          };
        });
        console.log('✅ Mensaje enviado y actualizado en Supabase');
      } else {
        // Si falla, remover mensaje optimista
        setConversation(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.filter(msg => msg.id !== optimisticMessage.id)
          };
        });
        console.error('❌ Error al enviar mensaje:', result.error);
      }
    }).catch(error => {
      console.error('❌ Error inesperado al enviar mensaje:', error);
      // Remover mensaje optimista en caso de error
      setConversation(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.filter(msg => msg.id !== optimisticMessage.id)
        };
      });
    });
  }, [conversation, currentUser.id, conversationId]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Cargando conversación...</h2>
          <p className="text-gray-600">Obteniendo mensajes desde Supabase</p>
        </div>
      </div>
    );
  }

  // Redirigir si no se encuentra la conversación
  if (!conversation) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Conversation not found</h2>
          <p className="text-gray-600 mb-6">The conversation you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate(ROUTES.CONVERSATIONS)}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
          >
            Back to Conversations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white">
      <ChatView
        conversation={conversation}
        currentUser={currentUser}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};